import { useEffect, useRef, useCallback } from 'react';

const PALETTE = ['#f2dfd3', '#e8d4c8', '#9acdc6', '#c8a99a', '#fff5ef', '#e8c088', '#d4b896'];

const GHOST_PALETTE = [
  '#fffdfa',
  '#f6f2ee',
  '#ebe6e2',
  '#e5eef0',
  '#dce8ea',
  '#efe8e4',
  '#f2f6f7',
];

const CRACKLE_PALETTE = ['#fffef8', '#f2dfd3', '#ffe8dc', '#e8f8f5', '#fff5e6', '#ddeef0'];

const GRAVITY = 170;
const GRAVITY_TRAIL = 98;
const STAR_DRAG = 2.15;
const ROCKET_DRAG = 0.12;

/** Ascent: subtle — small on screen so bursts are the hero moment. */
const ROCKET_WIGGLE_AMP = 52;
const ROCKET_WIGGLE_JITTER = 14;
const ROCKET_TRAIL_EMIT = 0.52;
const ROCKET_HISTORY_LEN = 5;
const ROCKET_HEAD_R = 0.82;
const ROCKET_GLOW_R = 0.34;
/** Burst richness: >1 = more stars + slightly wider spread than base profiles. */
const STAR_COUNT_FACTOR = 1.22;
const BURST_SPEED_SCALE = 1.1;

const TRAIL_SPARK_COLORS = [
  '#fffefb',
  '#fff2e6',
  '#ffe4cc',
  '#ffd4b0',
  '#ffc090',
  '#ff9a6e',
];

/** Weak perspective (CodePen-style 3D shell); +z = toward viewer → larger / brighter. */
const DEPTH_F = 460;
const DEPTH_DENOM_MIN = 128;
const PROJ_MIN = 0.48;
const PROJ_MAX = 1.36;

function normalize3(v) {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/**
 * Uniform sphere with +y = screen-up (matches burstVelocityFromDir + depthProject).
 * Pole must be on y, not z: old z=cosTheta put most radial motion in “depth” only, so
 * perspective smeared bursts into a horizontal band with little vertical spread.
 */
function dirChrysanthemum() {
  const u = Math.random();
  const v = Math.random();
  const phi = 2 * Math.PI * u;
  const cosTheta = 2 * v - 1;
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
  return {
    x: sinTheta * Math.cos(phi),
    y: cosTheta,
    z: sinTheta * Math.sin(phi),
  };
}

/** Lerp toward a fresh random sphere point — breaks disk / belt coplanarity (pancakes). */
function blendTowardSphere(primary, mix) {
  const s = dirChrysanthemum();
  const t = Math.max(0, Math.min(1, mix));
  return normalize3({
    x: primary.x * (1 - t) + s.x * t,
    y: primary.y * (1 - t) + s.y * t,
    z: primary.z * (1 - t) + s.z * t,
  });
}

/** Ring: equatorial bias but heavily thinned into full volume so it never reads as a flat line. */
function dirRing() {
  const a = Math.random() * Math.PI * 2;
  const y = (Math.random() - 0.5) * 0.72;
  const xz = Math.sqrt(Math.max(0.02, 1 - y * y));
  const v = normalize3({ x: Math.cos(a) * xz, y, z: Math.sin(a) * xz });
  return blendTowardSphere(v, 0.58 + Math.random() * 0.28);
}

/** Palm: strong upward bias (streams climbing before rain). */
function dirPalm() {
  if (Math.random() < 0.62) {
    const a = Math.random() * Math.PI * 2;
    const y = 0.28 + Math.random() * 0.72;
    const xz = Math.sqrt(Math.max(0.02, 1 - y * y));
    const disk = normalize3({ x: Math.cos(a) * xz, y, z: Math.sin(a) * xz });
    return blendTowardSphere(disk, 0.34 + Math.random() * 0.32);
  }
  return dirChrysanthemum();
}

/** Willow: long hang then droop — slight bias to arc downward over time (paired with low drag + gravity mult). */
function dirWillow() {
  const d = dirChrysanthemum();
  d.y -= Math.random() * 0.22;
  return normalize3(d);
}

/** Horse tail: dense tail following lift — bias outward + downward. */
function dirHorseTail() {
  const a = Math.random() * Math.PI * 2;
  const y = -0.35 - Math.random() * 0.55;
  const xz = Math.sqrt(Math.max(0.02, 1 - y * y));
  const v = normalize3({ x: Math.cos(a) * xz, y, z: Math.sin(a) * xz });
  return blendTowardSphere(v, 0.38 + Math.random() * 0.28);
}

/** Crossette: spiky arms — wide y spread + heavy sphere blend (raw crossette is very flat). */
function dirCrossette() {
  const arms = 6;
  const arm = Math.floor(Math.random() * arms);
  const base = (arm / arms) * Math.PI * 2;
  const jitter = (Math.random() - 0.5) * 0.55;
  const a = base + jitter;
  const y = (Math.random() - 0.5) * 0.72;
  const xz = Math.sqrt(Math.max(0.02, 1 - y * y));
  const v = normalize3({ x: Math.cos(a) * xz, y, z: Math.sin(a) * xz });
  return blendTowardSphere(v, 0.62 + Math.random() * 0.22);
}

/** Falling leaves: drift-heavy but still volumetric (was a thin horizontal disk). */
function dirFallingLeaves() {
  const a = Math.random() * Math.PI * 2;
  const y = (Math.random() - 0.5) * 0.62;
  const xz = Math.sqrt(Math.max(0.02, 1 - y * y));
  const v = normalize3({ x: Math.cos(a) * xz, y, z: Math.sin(a) * xz });
  return blendTowardSphere(v, 0.52 + Math.random() * 0.26);
}

function burstVelocityFromDir(dir, speed) {
  return {
    vx: dir.x * speed,
    vy: dir.y * speed,
    vz: dir.z * speed,
  };
}

/** Random yaw around vertical (+y) so each show’s bursts aim differently; keeps y-up sphere (no pancake). */
function rotateDirYaw(dir, yaw) {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return normalize3({
    x: dir.x * c - dir.z * s,
    y: dir.y,
    z: dir.x * s + dir.z * c,
  });
}

/** Tilt y↔z (rotate around +x) so disk-style shells aren’t locked parallel to the horizon. */
function rotateDirPitch(dir, pitch) {
  const c = Math.cos(pitch);
  const s = Math.sin(pitch);
  return normalize3({
    x: dir.x,
    y: dir.y * c - dir.z * s,
    z: dir.y * s + dir.z * c,
  });
}

const DISK_SHELL_KINDS = new Set(['ring', 'crossette', 'fallingLeaves', 'horseTail', 'palm']);

/** Small isotropic nudge so near-coplanar clouds gain thickness. */
function thickenDirection(dir, shellKind) {
  const base = DISK_SHELL_KINDS.has(shellKind) ? 0.1 : 0.038;
  const amt = base + Math.random() * (DISK_SHELL_KINDS.has(shellKind) ? 0.11 : 0.042);
  return normalize3({
    x: dir.x + triSigned() * amt,
    y: dir.y + triSigned() * amt,
    z: dir.z + triSigned() * amt,
  });
}

function depthProject(p) {
  const denom = Math.max(DEPTH_DENOM_MIN, DEPTH_F + p.z);
  let proj = DEPTH_F / denom;
  proj = Math.min(PROJ_MAX, Math.max(PROJ_MIN, proj));
  return {
    sx: p.ox + p.x * proj,
    sy: p.oy - p.y * proj,
    proj,
  };
}

/** Triangular-ish -1..1: clusters near 0, feels less grid-like than uniform. */
function triSigned() {
  return Math.random() + Math.random() - 1;
}

function sampleDirection(shellKind) {
  switch (shellKind) {
    case 'ring':
      return dirRing();
    case 'palm':
      return dirPalm();
    case 'willow':
      return dirWillow();
    case 'horseTail':
      return dirHorseTail();
    case 'crossette':
      return dirCrossette();
    case 'fallingLeaves':
      return dirFallingLeaves();
    case 'chrysanthemum':
    case 'ghost':
    case 'strobe':
    case 'crackle':
    default:
      return dirChrysanthemum();
  }
}

function inchFactors(inches) {
  if (inches >= 16) return { count: 1.38, speed: 1.16 };
  if (inches >= 12) return { count: 1.14, speed: 1.08 };
  if (inches <= 6) return { count: 0.74, speed: 0.9 };
  return { count: 1, speed: 1 };
}

const SHELL_POOL = [
  'chrysanthemum',
  'ring',
  'willow',
  'palm',
  'crackle',
  'crossette',
  'horseTail',
  'fallingLeaves',
];

/** Weighted shell sizes (CodePen-style mix of 6"–16"). */
const INCH_WEIGHTS = [6, 6, 8, 8, 8, 10, 10, 12, 12, 12, 14, 16, 16];

/** Sky flash intensity bump (MillerTime-style illumination) from a burst. */
function skyFlashBump(intensity, shellKind, inches, particleCount) {
  const size = inches >= 16 ? 0.5 : inches >= 12 ? 0.36 : inches >= 8 ? 0.26 : 0.2;
  const kind =
    shellKind === 'ghost'
      ? 0.42
      : shellKind === 'strobe'
        ? 0.48
        : shellKind === 'chrysanthemum' || shellKind === 'willow'
          ? 0.38
          : shellKind === 'palm' || shellKind === 'crackle'
            ? 0.35
            : 0.3;
  const countN = Math.min(0.22, particleCount / 850);
  const add = size * 0.32 + kind * 0.28 + countN;
  return Math.min(1, intensity + add);
}

/**
 * Subtle full-viewport wash: warm lift + cool zenith, breathes with time (CodePen sky lighting).
 */
function drawSkyLighting(ctx, w, h, intensity, nowMs) {
  const clamped = Math.min(1, Math.max(0, intensity));
  if (clamped <= 0.004) return;
  const breath = 1 + Math.sin(nowMs * 0.00085) * 0.07 * (0.25 + clamped);
  const i = clamped * breath;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  const zenith = 0.055 * i;
  const mid = 0.04 * i;
  const horizon = 0.095 * i;
  g.addColorStop(0, `rgba(120, 165, 205, ${zenith * 0.75})`);
  g.addColorStop(0.32, `rgba(130, 70, 88, ${mid * 0.45})`);
  g.addColorStop(0.72, `rgba(180, 95, 105, ${mid * 0.55})`);
  g.addColorStop(1, `rgba(255, 198, 168, ${horizon})`);
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

/**
 * Per-shell physics + visuals (aligned with Firework Simulator v2 shell names).
 */
function getShellProfile(shellKind, inches) {
  const { count: cMul, speed: sMul } = inchFactors(inches);

  const base = {
    shellKind,
    ghostRender: false,
    strobe: false,
    crackle: false,
    flutter: false,
    gravityMul: 1,
  };

  switch (shellKind) {
    case 'ghost':
      return {
        ...base,
        countMin: Math.floor(215 * cMul),
        countMax: Math.floor(295 * cMul),
        speedMin: 235 * sMul,
        speedMax: 340 * sMul,
        decayMin: 0.13,
        decayMax: 0.22,
        drag: 1.2,
        sizeMin: 1.35,
        sizeMax: 3.1,
        palette: GHOST_PALETTE,
        ghostRender: true,
      };

    case 'strobe':
      return {
        ...base,
        countMin: Math.floor(178 * cMul),
        countMax: Math.floor(232 * cMul),
        speedMin: 228 * sMul,
        speedMax: 332 * sMul,
        decayMin: 0.26,
        decayMax: 0.48,
        drag: STAR_DRAG * 0.96,
        sizeMin: 1.55,
        sizeMax: 3.25,
        palette: ['#ffffff', '#fffefb', '#f5fffc', '#fff8f2', '#e8faf7', '#ffffff'],
        strobe: true,
      };

    case 'ring': {
      return {
        ...base,
        countMin: Math.floor(135 * cMul),
        countMax: Math.floor(188 * cMul),
        speedMin: 228 * sMul,
        speedMax: 318 * sMul,
        decayMin: 0.52,
        decayMax: 0.92,
        drag: 2.05,
        sizeMin: 1.05,
        sizeMax: 2.35,
        palette: PALETTE,
      };
    }

    case 'palm': {
      return {
        ...base,
        countMin: Math.floor(158 * cMul),
        countMax: Math.floor(208 * cMul),
        speedMin: 248 * sMul,
        speedMax: 348 * sMul,
        decayMin: 0.45,
        decayMax: 0.82,
        drag: 1.78,
        sizeMin: 1.15,
        sizeMax: 2.55,
        palette: PALETTE,
      };
    }

    case 'willow': {
      return {
        ...base,
        countMin: Math.floor(195 * cMul),
        countMax: Math.floor(268 * cMul),
        speedMin: 175 * sMul,
        speedMax: 268 * sMul,
        decayMin: 0.22,
        decayMax: 0.38,
        drag: 1.38,
        sizeMin: 1.2,
        sizeMax: 2.65,
        palette: PALETTE,
        gravityMul: 1.14,
      };
    }

    case 'horseTail': {
      return {
        ...base,
        countMin: Math.floor(165 * cMul),
        countMax: Math.floor(218 * cMul),
        speedMin: 210 * sMul,
        speedMax: 305 * sMul,
        decayMin: 0.42,
        decayMax: 0.78,
        drag: 1.92,
        sizeMin: 1.1,
        sizeMax: 2.4,
        palette: PALETTE,
        gravityMul: 1.22,
      };
    }

    case 'crossette': {
      return {
        ...base,
        countMin: Math.floor(118 * cMul),
        countMax: Math.floor(168 * cMul),
        speedMin: 232 * sMul,
        speedMax: 322 * sMul,
        decayMin: 0.55,
        decayMax: 0.95,
        drag: 2.25,
        sizeMin: 1,
        sizeMax: 2.2,
        palette: PALETTE,
      };
    }

    case 'crackle': {
      return {
        ...base,
        countMin: Math.floor(138 * cMul),
        countMax: Math.floor(188 * cMul),
        speedMin: 205 * sMul,
        speedMax: 298 * sMul,
        decayMin: 0.62,
        decayMax: 1.05,
        drag: 2.35,
        sizeMin: 0.95,
        sizeMax: 2.05,
        palette: CRACKLE_PALETTE,
        crackle: true,
      };
    }

    case 'fallingLeaves': {
      return {
        ...base,
        countMin: Math.floor(120 * cMul),
        countMax: Math.floor(168 * cMul),
        speedMin: 118 * sMul,
        speedMax: 198 * sMul,
        decayMin: 0.32,
        decayMax: 0.52,
        drag: 1.48,
        sizeMin: 1.25,
        sizeMax: 2.8,
        palette: [...PALETTE, '#b8d4cf', '#c9e4df'],
        gravityMul: 0.88,
        flutter: true,
      };
    }

    case 'chrysanthemum':
    default: {
      const is16 = inches >= 16;
      if (is16) {
        return {
          ...base,
          shellKind: 'chrysanthemum',
          countMin: Math.floor(188 * cMul),
          countMax: Math.floor(258 * cMul),
          speedMin: 248 * sMul,
          speedMax: 352 * sMul,
          decayMin: 0.5,
          decayMax: 0.96,
          drag: STAR_DRAG,
          sizeMin: 1.2,
          sizeMax: 2.85,
          palette: PALETTE,
        };
      }
      return {
        ...base,
        shellKind: 'chrysanthemum',
        countMin: Math.floor(92 * cMul),
        countMax: Math.floor(128 * cMul),
        speedMin: 198 * sMul,
        speedMax: 292 * sMul,
        decayMin: 0.52,
        decayMax: 0.92,
        drag: STAR_DRAG,
        sizeMin: 1.05,
        sizeMax: 2.45,
        palette: PALETTE,
      };
    }
  }
}

function solveLaunchVelocity(x0, y0, tx, ty, g) {
  const dy = y0 - ty;
  if (dy <= 2) {
    return { vx: (tx - x0) * 0.35, vy: -440 };
  }
  const vy0 = -Math.sqrt(2 * g * dy);
  const tPeak = -vy0 / g;
  const vx0 = (tx - x0) / tPeak;
  return { vx: vx0, vy: vy0 };
}

function getTitleBurstAnchors() {
  const el = document.querySelector('#invite .hero-invite__reveal--title');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 8 || r.height < 8) return null;
  const padLo = 20;
  const padHi = 54;
  const leftPad = padLo + Math.random() * (padHi - padLo);
  const rightPad = padLo + Math.random() * (padHi - padLo);
  /** Vertical band where “New Year’s Eve” lettering actually sits (avoid SVG padding + low pancakes). */
  const yCoreLo = r.top + r.height * 0.36;
  const yCoreHi = r.top + r.height * 0.58;
  return {
    leftX: r.left - leftPad,
    rightX: r.right + rightPad,
    midY: r.top + r.height * (0.4 + Math.random() * 0.18),
    yCoreLo,
    yCoreHi,
    top: r.top,
    bottom: r.bottom,
    left: r.left,
    right: r.right,
    w: r.width,
    h: r.height,
  };
}

export function HeroFireworks() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const runningRef = useRef(false);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    resize();
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const rockets = [];
    const particles = [];
    const trailSparks = [];
    let skyIntensity = 0;

    const vw = () => window.innerWidth;
    const vh = () => window.innerHeight;

    /** New each mount / hero replay — direction, size, timing, aim. */
    const session = {
      dirYaw: Math.random() * Math.PI * 2,
      dirPitch: (Math.random() - 0.5) * 0.62,
      countMul: 0.88 + Math.random() * 0.32,
      speedMul: 0.94 + Math.random() * 0.22,
      inchShift: Math.floor(Math.random() * 9) - 4,
      burstSkewX: triSigned() * 42,
      burstSkewY: triSigned() * 36,
      spreadLo: 20 + Math.random() * 28,
      spreadExtra: 22 + Math.random() * 38,
      launchDepthLo: 4 + Math.random() * 8,
      launchDepthPow: 0.65 + Math.random() * 0.22,
      launchDepthMax: 28 + Math.random() * 22,
    };

    const pickRandomShellSpec = () => {
      if (Math.random() < 0.07) {
        const gIn = [10, 12, 12, 14, 14, 16][Math.floor(Math.random() * 6)];
        return { type: 'ghost', inches: gIn };
      }
      const type = SHELL_POOL[Math.floor(Math.random() * SHELL_POOL.length)];
      let iIdx = Math.floor(Math.random() * INCH_WEIGHTS.length);
      iIdx = Math.max(0, Math.min(INCH_WEIGHTS.length - 1, iIdx + session.inchShift));
      return { type, inches: INCH_WEIGHTS[iIdx] };
    };

    const arm = (shell, side) => {
      const W = vw();
      const H = vh();
      const anchors = getTitleBurstAnchors();
      const spreadH = session.spreadLo + Math.random() * session.spreadExtra;

      let burstX;
      let burstY;
      if (anchors) {
        const span = anchors.yCoreHi - anchors.yCoreLo;
        const u = Math.random();
        const v = Math.random();
        const midBias = (u + v) * 0.5;
        let burstYRaw = anchors.yCoreLo + midBias * span;
        burstYRaw += triSigned() * (3 + Math.random() * 8) + session.burstSkewY * 0.1;
        burstY = Math.min(anchors.yCoreHi + 4, Math.max(anchors.yCoreLo - 3, burstYRaw));

        if (side === 'left') {
          burstX =
            anchors.leftX +
            triSigned() * spreadH +
            Math.random() * anchors.w * (0.02 + Math.random() * 0.2) +
            session.burstSkewX * 0.28;
        } else {
          burstX =
            anchors.rightX -
            triSigned() * spreadH -
            Math.random() * anchors.w * (0.02 + Math.random() * 0.2) +
            session.burstSkewX * 0.28;
        }
        if (Math.random() < 0.2) {
          const cx = anchors.left + anchors.w * 0.5;
          burstX += (cx - burstX) * (0.1 + Math.random() * 0.32);
        }
      } else {
        const jx = triSigned() * (26 + Math.random() * 34) + session.burstSkewX * 0.4;
        const jy = triSigned() * (22 + Math.random() * 28) + session.burstSkewY * 0.4;
        burstX = side === 'left' ? W * (0.22 + Math.random() * 0.14) + jx : W * (0.64 + Math.random() * 0.14) + jx;
        burstY = H * (0.28 + Math.random() * 0.14) + jy;
      }

      const launchY =
        H -
        (session.launchDepthLo +
          Math.random() ** session.launchDepthPow * session.launchDepthMax);
      let launchX;
      if (side === 'left') {
        const lo = 0.045 + Math.random() * 0.055;
        const hi = 0.28 + Math.random() * 0.2;
        launchX = W * (lo + Math.random() * (hi - lo));
      } else {
        const lo = 0.55 + Math.random() * 0.08;
        const hi = 0.94 + Math.random() * 0.045;
        launchX = W * (lo + Math.random() * (hi - lo));
      }
      launchX += triSigned() * (16 + Math.random() * 26);

      const { vx, vy } = solveLaunchVelocity(launchX, launchY, burstX, burstY, GRAVITY);

      rockets.push({
        x: launchX,
        y: launchY,
        vx,
        vy,
        shell,
        burstX,
        burstY,
        targetY: burstY,
        spawnTime: performance.now(),
        wigglePhase: Math.random() * Math.PI * 2,
        wiggleFreq: 0.95 + Math.random() * 1.15,
        hist: [],
      });
      startLoop();
    };

    const burst = (mx, my, shell) => {
      const kind = shell.type || 'chrysanthemum';
      const inches = shell.inches ?? 8;
      const p = getShellProfile(kind, inches);
      const rawCount = p.countMin + Math.floor(Math.random() * (p.countMax - p.countMin + 1));
      const count = Math.max(
        22,
        Math.floor(rawCount * STAR_COUNT_FACTOR * session.countMul),
      );

      skyIntensity = skyFlashBump(skyIntensity, p.shellKind, inches, count);

      for (let i = 0; i < count; i++) {
        let dir = sampleDirection(p.shellKind);
        dir = rotateDirPitch(dir, session.dirPitch);
        dir = rotateDirYaw(dir, session.dirYaw);
        dir = thickenDirection(dir, p.shellKind);
        const t = Math.random();
        const speed =
          BURST_SPEED_SCALE *
          session.speedMul *
          (p.speedMin +
            t * t * (p.speedMax - p.speedMin) * (0.62 + Math.random() * 0.48));
        const { vx, vy, vz } = burstVelocityFromDir(dir, speed);
        /**
         * Firework Simulator v2: stars stay solid briefly, then strobe (on:off:off) on remaining life.
         * strobeEnterLife: while life > this, no flicker; below, slice-based pattern.
         */
        const strobeEnterLife = 0.48 + Math.random() * 0.24;
        const strobeSlice = 0.011 + Math.random() * 0.019;
        particles.push({
          ox: mx,
          oy: my,
          x: 0,
          y: 0,
          z: 0,
          vx,
          vy,
          vz,
          life: 1,
          decay: p.decayMin + Math.random() * (p.decayMax - p.decayMin),
          color: p.palette[Math.floor(Math.random() * p.palette.length)],
          size: p.sizeMin + Math.random() * (p.sizeMax - p.sizeMin),
          drag: p.drag,
          ghostRender: p.ghostRender,
          strobe: true,
          crackle: p.crackle,
          flutter: p.flutter,
          gravityMul: p.gravityMul,
          strobeEnterLife,
          strobeSlice,
          shellKind: p.shellKind,
          cracklePhase: Math.random() * Math.PI * 2,
          flutterPhase: Math.random() * Math.PI * 2,
        });
      }
    };

    const startLoop = () => {
      if (runningRef.current) return;
      runningRef.current = true;
      let last = performance.now();

      const tick = (now) => {
        const dt = Math.min(0.035, (now - last) / 1000);
        last = now;

        const w = vw();
        const h = vh();
        const dpr = canvas.width / w;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        skyIntensity *= Math.exp(-2.05 * dt);
        const ambientPulse =
          (rockets.length + particles.length + trailSparks.length > 0 ? 0.035 : 0) *
          (0.5 + 0.5 * Math.sin(now * 0.0014));
        drawSkyLighting(ctx, w, h, Math.min(1, skyIntensity + ambientPulse), now);

        const rocketDrag = Math.exp(-ROCKET_DRAG * dt);
        const trailDrag = Math.exp(-1.35 * dt);

        for (let i = trailSparks.length - 1; i >= 0; i--) {
          const s = trailSparks[i];
          s.vy += GRAVITY_TRAIL * dt;
          s.vx *= trailDrag;
          s.vy *= trailDrag;
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          s.life -= s.decay * dt;
          if (s.life <= 0) {
            trailSparks.splice(i, 1);
          }
        }

        for (let i = rockets.length - 1; i >= 0; i--) {
          const r = rockets[i];
          const wf = r.wiggleFreq;
          const wMain =
            Math.sin(now * 0.00165 * wf + r.wigglePhase) * ROCKET_WIGGLE_AMP +
            Math.cos(now * 0.00095 * wf * 1.31 + r.wigglePhase * 1.07) *
              ROCKET_WIGGLE_AMP *
              0.44;
          r.vx += wMain * dt + triSigned() * ROCKET_WIGGLE_JITTER * dt;

          r.vy += GRAVITY * dt;
          r.vx *= rocketDrag;
          r.x += r.vx * dt;
          r.y += r.vy * dt;

          const speed = Math.hypot(r.vx, r.vy) || 1;
          r.trailAcc = (r.trailAcc || 0) + ROCKET_TRAIL_EMIT * dt * (1.1 + speed * 0.0018);
          while (r.trailAcc >= 1) {
            r.trailAcc -= 1;
            const backX = -r.vx / speed;
            const backY = -r.vy / speed;
            trailSparks.push({
              x: r.x - backX * (2.2 + Math.random() * 2.4) + triSigned() * 1.8,
              y: r.y - backY * (2.2 + Math.random() * 2.4) + triSigned() * 1.5,
              vx: backX * (18 + Math.random() * 26) + triSigned() * 16,
              vy: backY * (18 + Math.random() * 26) + triSigned() * 14,
              life: 1,
              decay: 2.85 + Math.random() * 2.9,
              size: (0.28 + Math.random() * 0.42) * 0.48,
              color: TRAIL_SPARK_COLORS[Math.floor(Math.random() * TRAIL_SPARK_COLORS.length)],
            });
          }

          r.hist.push({ x: r.x, y: r.y });
          while (r.hist.length > ROCKET_HISTORY_LEN) {
            r.hist.shift();
          }

          const reachedHeight = r.y <= r.targetY + 3;
          const timedOut = now - r.spawnTime > 4800;
          if (reachedHeight || timedOut) {
            const detJ = 5 + Math.random() * 14;
            burst(
              r.burstX + triSigned() * detJ,
              r.burstY + triSigned() * detJ * 0.72,
              r.shell,
            );
            rockets.splice(i, 1);
          }
        }

        ctx.globalCompositeOperation = 'lighter';

        for (let i = 0; i < trailSparks.length; i++) {
          const s = trailSparks[i];
          const a = Math.max(0, Math.min(1, s.life * 0.88));
          ctx.globalAlpha = a * 0.32;
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
          ctx.fill();
        }

        for (let i = 0; i < rockets.length; i++) {
          const r = rockets[i];
          const h = r.hist;
          if (h.length >= 2) {
            for (let j = 1; j < h.length; j++) {
              const t = j / (h.length - 1);
              ctx.globalAlpha = (0.03 + t * 0.18) * 0.75;
              ctx.strokeStyle = `rgba(255, 218, 190, ${0.12 + t * 0.28})`;
              ctx.lineWidth = 0.35 + t * 0.75;
              ctx.lineCap = 'round';
              ctx.beginPath();
              ctx.moveTo(h[j - 1].x, h[j - 1].y);
              ctx.lineTo(h[j].x, h[j].y);
              ctx.stroke();
            }
          }
          ctx.globalAlpha = 0.58;
          ctx.fillStyle = '#fff9f4';
          ctx.beginPath();
          ctx.arc(r.x, r.y, ROCKET_HEAD_R, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 0.2;
          ctx.fillStyle = '#ffd4b8';
          ctx.beginPath();
          ctx.arc(r.x + 0.12, r.y + 0.18, ROCKET_GLOW_R, 0, Math.PI * 2);
          ctx.fill();
        }

        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          const dragK = p.drag != null ? p.drag : STAR_DRAG;
          const dragFactor = Math.exp(-dragK * dt);
          const gMul = p.gravityMul ?? 1;

          p.vy -= GRAVITY * dt * gMul;
          if (p.flutter) {
            p.vx += Math.sin(now * 0.0038 + p.flutterPhase) * 52 * dt;
          }
          p.vx *= dragFactor;
          p.vy *= dragFactor;
          p.vz *= dragFactor;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.z += p.vz * dt;
          p.life -= p.decay * dt;
          if (p.life <= 0) {
            particles.splice(i, 1);
          }
        }

        particles.sort((a, b) => a.z - b.z);

        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const { sx, sy, proj } = depthProject(p);

          let drawAlpha = Math.max(0, Math.min(1, p.life * 0.92));
          drawAlpha *= 0.52 + 0.48 * proj;
          if (p.ghostRender) {
            drawAlpha *= 0.58;
          }

          let strobeOn = true;
          if (p.strobe && p.strobeSlice > 0) {
            if (p.life > p.strobeEnterLife) {
              strobeOn = true;
            } else {
              strobeOn = Math.floor(p.life / p.strobeSlice) % 3 === 0;
            }
            drawAlpha *= strobeOn ? 1 : 0.22;
            if (strobeOn) {
              drawAlpha = Math.min(1, drawAlpha * 1.22);
            }
          }

          if (p.crackle) {
            const c1 = 0.5 + 0.5 * Math.sin(now * 0.052 + p.cracklePhase);
            const c2 = 0.5 + 0.5 * Math.sin(now * 0.28 + p.cracklePhase * 1.7);
            drawAlpha *= 0.38 + 0.62 * c1 * (0.55 + 0.45 * c2);
          }

          ctx.globalAlpha = drawAlpha;
          ctx.fillStyle = p.strobe && strobeOn ? '#ffffff' : p.color;
          const s = p.size;
          let radius = s * 0.52 * proj;
          if (p.strobe && strobeOn) radius = s * 0.62 * proj;
          if (p.shellKind === 'crackle' && drawAlpha > 0.35) {
            radius *= 1.05;
          }
          ctx.beginPath();
          ctx.arc(sx, sy, radius, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;

        if (rockets.length > 0 || particles.length > 0 || trailSparks.length > 0) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          runningRef.current = false;
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    const timers = [];
    const shellCount = 10;
    let t = 240 + Math.floor(Math.random() * 200);
    const stepLo = 52 + Math.floor(Math.random() * 28);
    const stepHi = stepLo + 18 + Math.floor(Math.random() * 42);
    const jitterMax = 32 + Math.floor(Math.random() * 48);
    for (let i = 0; i < shellCount; i++) {
      const side = Math.random() < 0.5 ? 'left' : 'right';
      const spec = pickRandomShellSpec();
      const delay = t + Math.floor(Math.random() * jitterMax);
      timers.push(
        window.setTimeout(() => {
          arm(spec, side);
        }, delay),
      );
      t += stepLo + Math.floor(Math.random() * (stepHi - stepLo));
    }

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      runningRef.current = false;
      rockets.length = 0;
      particles.length = 0;
      trailSparks.length = 0;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [resize]);

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return null;

  return (
    <div
      ref={wrapRef}
      className="hero-fireworks-viewport"
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="hero-fireworks-viewport__canvas" />
    </div>
  );
}
